import styled from "styled-components";


export const Container = styled.section`
  margin-top: 12rem;

  h2{
    text-align: center;
    font-size: 4rem;
    margin-bottom: 5rem;
  }

  .featured,
  .oss-grid{
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2.4rem;
    max-width: 110rem;
    margin: 0 auto;

    /* ScrollAnimation renders a wrapper div around each article, so the grid items are those wrappers */
    & > div{
      height: 100%;
    }
  }

  /* Only the lead project spans both columns */
  .featured > div:first-child{
    grid-column: 1 / -1;
  }

  .project{
    /* Cards stay dark in both themes, so the text accent stays light inside them */
    --accent-text: #8ab4ff;
    background: #2b2b2b;
    color: #fff;
    border: 1px solid transparent;
    border-radius: 1.2rem;
    padding: 2.8rem 2.6rem;
    display: flex;
    flex-direction: column;
    height: 100%;
    transition: transform 0.25s, border-color 0.25s;

    &:hover{
      border-color: var(--green);
    }

    @media (prefers-reduced-motion: no-preference){
      &:hover{
        transform: translateY(-4px);
      }
    }

    /* Mirrors the .terminal panel of the open-source cards */
    .project-media{
      margin: 0 0 2.4rem;
      border-radius: 0.8rem;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.08);
      background: #161616;
      aspect-ratio: 960 / 500;

      img{
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top center;
      }
    }

    h3{
      font-size: 2.2rem;
      margin-bottom: 1.4rem;
    }

    .stat{
      font-family: 'Red Hat Mono', monospace;
      font-size: 1.5rem;
      color: var(--accent-text);
      margin: 0 0 1.6rem;
      letter-spacing: 0.02em;
    }

    /* Tightens the title's 1.4rem bottom margin to 0.8rem when a stat follows */
    h3 + .stat{
      margin-top: -0.6rem;
    }

    .description{
      line-height: 1.6;
      opacity: 0.85;
      margin-bottom: 2.4rem;

      p + p{
        margin-top: 1.4rem;
      }
    }

    .tech-list{
      font-family: 'Red Hat Mono', monospace;
      font-size: 1.35rem;
      opacity: 0.8;
      display: flex;
      flex-wrap: wrap;
      gap: 0.8rem 1.6rem;
      margin-top: auto;
    }

    .project-links{
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      margin-top: 2.4rem;

      a{
        display: inline-block;
        font-family: 'Red Hat Mono', monospace;
        font-size: 1.3rem;
        padding: 0.7rem 1.4rem;
        border: 1px solid rgba(255, 255, 255, 0.4);
        border-radius: 10rem;
        color: #fff;
        transition: border-color 0.25s, color 0.25s;

        &:hover,
        &:focus-visible{
          border-color: var(--accent-text);
          color: var(--accent-text);
        }
      }
    }
  }

  .project-lead{
    padding: 4rem 3.6rem;
    border-color: rgba(47, 110, 206, 0.45);

    h3{
      font-size: 3.4rem;
      margin-bottom: 0;
    }

    h3 + .stat{
      margin: 0.8rem 0 2rem;
    }

    .description{
      max-width: 62rem;
    }
  }

  /* Lead card with a phone screenshot bleeding off the bottom edge, clipped by the card radius */
  .project.has-phone{
    flex-direction: row;
    align-items: stretch;
    gap: 4rem;
    overflow: hidden;
    padding-bottom: 0;
    min-height: 44rem;

    .project-content{
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      padding-bottom: 4rem;
    }
  }

  /* Lead card title row: app icon next to the title and stat */
  .project .app-title{
    display: flex;
    align-items: center;
    gap: 1.8rem;
    margin-bottom: 2.4rem;

    img{
      flex: 0 0 auto;
      width: 5.6rem;
      height: 5.6rem;
      border-radius: 1.3rem;
    }

    h3{
      margin: 0;
    }

    h3 + .stat{
      margin: 0.4rem 0 0;
    }
  }

  /* Key numbers: the value reads first visually, DOM order stays dt then dd */
  .project .facts{
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2.4rem;
    /* The description above already contributes 2.4rem (flex margins do not collapse) */
    margin: 0.8rem 0 3.2rem;
    padding: 2.4rem 0;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);

    .fact{
      display: flex;
      flex-direction: column-reverse;
      justify-content: flex-end;
    }

    dd{
      font-size: 2.6rem;
      font-weight: 700;
      line-height: 1.1;
      color: #fff;
    }

    dt{
      font-family: 'Red Hat Mono', monospace;
      font-size: 1.25rem;
      line-height: 1.4;
      opacity: 0.7;
      margin-top: 0.4rem;
    }
  }

  .project-phone{
    flex: 0 0 28rem;
    align-self: flex-end;
    height: 40rem;
    margin-right: 2rem;
    border: 0.8rem solid #0f0f0f;
    border-bottom: 0;
    border-radius: 3.2rem 3.2rem 0 0;
    overflow: hidden;
    background: #0f0f0f;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.12), 0 2rem 6rem rgba(0, 0, 0, 0.5);

    /* The screenshot is taller than the frame on purpose: only the top of the app shows */
    img{
      display: block;
      width: 100%;
      height: auto;
      border-radius: 2.4rem 2.4rem 0 0;
    }
  }

  .project-links a:focus-visible,
  .copy-button:focus-visible,
  .side-list a:focus-visible{
    outline: 2px solid var(--accent-text);
    outline-offset: 3px;
  }

  .oss{
    .oss-heading{
      margin-top: 12rem;
      margin-bottom: 1.6rem;
    }

    .oss-intro{
      text-align: center;
      font-size: 1.8rem;
      opacity: 0.8;
      margin: 0 auto 4.8rem;
      max-width: 60rem;
    }
  }

  .oss-card{
    .terminal{
      margin-bottom: 2.4rem;
      min-height: 19rem;
      background: #161616;
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 0.8rem;
      font-family: 'Red Hat Mono', monospace;
      font-size: 1.35rem;
      line-height: 1.7;
    }

    .terminal-bar{
      display: flex;
      gap: 0.7rem;
      padding: 1.2rem 1.8rem 0;

      span{
        width: 0.9rem;
        height: 0.9rem;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.18);
      }
    }

    .terminal-body{
      padding: 1.6rem 1.8rem;
    }

    .terminal-line{
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }

    .terminal-cmd,
    .terminal-q{
      color: #fff;
    }

    .terminal-prompt{
      color: var(--accent-text);
    }

    .terminal-out{
      color: rgba(255, 255, 255, 0.6);
    }

    .terminal-answer{
      color: rgba(255, 255, 255, 0.8);
    }

    .terminal-ok{
      color: #6fd39a;
    }

    h3{
      margin-bottom: 0.8rem;
    }

    .oss-meta{
      font-family: 'Red Hat Mono', monospace;
      font-size: 1.3rem;
      color: var(--accent-text);
      margin-bottom: 2rem;
    }

    .install{
      display: flex;
      align-items: stretch;
      gap: 1rem;
      margin-bottom: 1.2rem;

      code{
        flex: 1;
        min-width: 0;
        font-family: 'Red Hat Mono', monospace;
        font-size: 1.4rem;
        background: #161616;
        padding: 1rem 1.4rem;
        border-radius: 0.6rem;
        overflow-x: auto;
        white-space: nowrap;
      }
    }

    .copy-button{
      background-color: transparent;
      border: 1px solid rgba(255, 255, 255, 0.4);
      color: #fff;
      font-family: 'Red Hat Mono', monospace;
      font-size: 1.3rem;
      padding: 0.9rem 1.4rem;
      border-radius: 0.6rem;
      transition: border-color 0.25s, color 0.25s;

      &:hover{
        filter: none;
        border-color: var(--accent-text);
        color: var(--accent-text);
      }
    }

    .install-note{
      font-size: 1.4rem;
      opacity: 0.8;
      margin-bottom: 2.4rem;

      code{
        font-family: 'Red Hat Mono', monospace;
        color: #fff;
      }
    }
  }

  .side-title{
    margin: 9rem auto 2.4rem;
    max-width: 110rem;
    font-size: 1.6rem;
    font-family: 'Red Hat Mono', monospace;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    opacity: 0.6;
  }

  .side-list{
    max-width: 110rem;
    margin: 0 auto;

    li{
      display: grid;
      grid-template-columns: 22rem 1fr auto;
      gap: 2.4rem;
      align-items: baseline;
      padding: 2rem 0;
      border-top: 1px solid rgba(128, 128, 128, 0.3);

      &:last-child{
        border-bottom: 1px solid rgba(128, 128, 128, 0.3);
      }
    }

    .side-name{
      font-weight: 500;
      font-size: 1.8rem;
    }

    .side-body p{
      line-height: 1.6;
    }

    .side-tech{
      font-family: 'Red Hat Mono', monospace;
      font-size: 1.3rem;
      opacity: 0.75;
      margin-top: 0.6rem;
    }

    .side-links{
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 0.8rem 1.8rem;

      a{
        font-family: 'Red Hat Mono', monospace;
        font-size: 1.4rem;
        color: var(--accent-text);
        white-space: nowrap;

        &:hover{
          text-decoration: underline;
        }
      }
    }
  }

  @media (max-width: 740px){
    .featured,
    .oss-grid{
      grid-template-columns: minmax(0, 1fr);
    }

    .project-lead{
      padding: 2.8rem 2.2rem;

      h3{
        font-size: 2.6rem;
      }
    }

    .project.has-phone{
      flex-direction: column;
      gap: 3.2rem;
      padding-bottom: 0;

      .project-content{
        padding-bottom: 0;
      }
    }

    .project .facts{
      gap: 1.6rem;

      dd{
        font-size: 2.2rem;
      }
    }

    .project-phone{
      flex: 0 0 auto;
      align-self: center;
      width: 24rem;
      height: 30rem;
      margin-right: 0;
    }

    .side-title,
    .side-list{
      display: none;
    }
  }

  @media (max-width: 400px){
    .project .facts{
      grid-template-columns: minmax(0, 1fr);
      gap: 1.2rem;
    }
  }
`
